// src/lib/native/voiceNoteCapture.ts — JS bridge for the in-car microphone foreground service.
//
// FEATURE 3b. Wires the native VoiceNoteCaptureService (Android MediaRecorder foreground service,
// launchable from a quick-settings tile or its persistent-notification Stop action while docked) into
// the SAME capture pipeline a CaptureSheet recording uses. A note captured from the tile/notification —
// even with the app killed — reaches Deepgram exactly like an in-app one.
//
// HOW IT REACHES THE PIPELINE (reuse, don't reinvent):
//   1. The native service records AAC/m4a to an app-internal file and records it as a durable "pending
//      capture" (path/uri/duration/mime) in SharedPreferences (survives app death).
//   2. This bridge drains those pending captures — triggered live by the plugin's "voiceNoteCaptured"
//      event when the WebView is alive, and on cold start / resume via drainVoiceNotes() — by reading
//      each file into a Blob and ENQUEUEING it into the existing offline capture queue
//      (src/lib/native/offlineQueue) with the exact CaptureQueueMeta shape CaptureSheet persists.
//   3. It then hands off to the existing drainCaptureQueue() (createMobileSource -> resumable upload ->
//      finalize-upload), which owns retry/offline-resilience. Once a capture's bytes are safely in the
//      offline queue's encrypted store, the native pending record + its file are cleared.
//
// CONSENT: an in-car note is a first-party self-memo (the capturer dictating their own note), so it
// carries an `internal_only` consent envelope — the same lawful basis the "Quick voice memo" path uses.
// This satisfies the fail-closed consent gate (drainCaptures parks a manual_audio item with no consent
// envelope; the server consent-guard requires a live consent row for manual_audio mobile captures).
//
// HARD RULE: every native access is behind isNative() + dynamic import + try/catch, so the website build
// never hard-depends on the plugin and this module no-ops on web.

import { isNative } from './platform';
import { enqueue } from './offlineQueue';
import { supabase } from '@/integrations/supabase/client';
import { drainCaptureQueue } from '@/lib/capture/drainCaptures';
import { CURRENT_DISCLOSURE_VERSION, type Consent } from '@/lib/capture';

// One native pending capture as returned by the plugin's drainPending().
interface PendingCapture {
  id: string;
  path?: string;
  uri?: string;
  mimeType?: string;
  durationMs?: number;
  size?: number;
  createdAt?: number;
}

interface VoiceNoteCapturePlugin {
  start(): Promise<{ recording: boolean }>;
  stop(): Promise<{ recording: boolean }>;
  isRecording(): Promise<{ recording: boolean }>;
  drainPending(): Promise<{ captures: PendingCapture[] }>;
  clearPending(options: { id: string }): Promise<void>;
  addListener(
    eventName: 'voiceNoteCaptured',
    listener: (capture: PendingCapture) => void,
  ): Promise<{ remove: () => Promise<void> }>;
}

async function getPlugin(): Promise<VoiceNoteCapturePlugin | null> {
  if (!isNative()) return null;
  try {
    const { registerPlugin } = await import('@capacitor/core');
    // registerPlugin returns a proxy even if the native side is missing; call sites swallow rejections.
    return registerPlugin<VoiceNoteCapturePlugin>('VoiceNoteCapture');
  } catch {
    return null;
  }
}

function base64ToBlob(b64: string, contentType?: string): Blob {
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return new Blob([bytes], contentType ? { type: contentType } : undefined);
}

// Read a native file (absolute path / file:// uri) into a Blob via @capacitor/filesystem.
async function readFileToBlob(cap: PendingCapture): Promise<Blob | null> {
  const target = cap.uri || cap.path;
  if (!target) return null;
  try {
    const { Filesystem } = await import('@capacitor/filesystem');
    // No `directory` → Filesystem resolves the absolute path / file:// uri directly. Returns base64.
    const { data } = await Filesystem.readFile({ path: target });
    const b64 = typeof data === 'string' ? data : '';
    if (!b64) return null;
    return base64ToBlob(b64, cap.mimeType || 'audio/mp4');
  } catch {
    return null;
  }
}

// The in-car note's consent envelope: a first-party self-memo (internal_only), mirroring the voice-memo
// path. `internal_only` is not a third-party basis, but supplying a live consent row keeps the capture
// on the same fail-closed contract as every other manual_audio source.
function inCarConsent(): Consent {
  return {
    basis: 'internal_only',
    parties: ['self'],
    jurisdiction: 'GB',
    disclosureVersion: CURRENT_DISCLOSURE_VERSION,
    verbalSegmentPresent: false,
    emotionInferenceOk: false,
  };
}

// Single-flight guard: the drain is triggered from several places (live event, cold start, resume) that
// can fire close together. Overlapping drains could both read the same pending capture and double-enqueue
// it. Serialise so only one runs at a time; overlapping calls no-op and a later trigger re-runs.
let draining = false;

/**
 * Drain all durable in-car voice-note captures into the existing upload pipeline. Reads each pending
 * capture's file into a Blob, enqueues it into the offline capture queue (which persists the bytes at
 * rest), clears the native record, then hands off to drainCaptureQueue() for create/upload/finalize.
 *
 * No-op on web, when signed out (nothing to attribute the upload to — captures stay pending for a later
 * signed-in drain), or when there are no pending captures. Never throws (fire-and-forget from callers).
 * Returns the number of captures handed to the offline queue this pass.
 */
export async function drainVoiceNotes(): Promise<number> {
  if (draining) return 0;
  draining = true;
  try {
    const plugin = await getPlugin();
    if (!plugin) return 0;

    let pending: PendingCapture[] = [];
    try {
      const res = await plugin.drainPending();
      pending = Array.isArray(res?.captures) ? res.captures : [];
    } catch {
      return 0;
    }
    if (pending.length === 0) return 0;

    // Attribute uploads to the signed-in user. If signed out, leave the captures pending natively — a
    // later drain (post-login) recovers them; never enqueue audio we can't attribute.
    const { data } = await supabase.auth.getSession();
    const ownerId = data.session?.user?.id;
    if (!ownerId) return 0;

    let enqueued = 0;
    for (const cap of pending) {
      if (!cap?.id) continue;
      const blob = await readFileToBlob(cap);
      if (!blob || blob.size === 0) {
        // Unreadable / empty payload — drop the stranded record so it doesn't loop forever (a source
        // with no audio would only strand server-side; nothing was recoverable here).
        try {
          await plugin.clearPending({ id: cap.id });
        } catch {
          /* best-effort */
        }
        continue;
      }

      const contentType = cap.mimeType || 'audio/mp4';
      try {
        // Enqueue into the SAME offline queue CaptureSheet uses, with the identical CaptureQueueMeta
        // shape so drainCaptureQueue reconstructs the source (and its consent basis) exactly as the
        // foreground flow would. capture_profile:'in_car' distinguishes it in raw metadata.
        await enqueue({
          id: cap.id, // stable uuid from the native service; the pipeline's idempotency key
          state: 'pending_upload',
          contentType,
          blob,
          meta: {
            ownerId,
            mode: 'memo',
            durationMs: cap.durationMs ?? 0,
            consent: inCarConsent(),
            sourceType: 'manual_audio',
            title: 'In-car voice note',
            rawMetadata: { capture_profile: 'in_car' },
            eventId: null,
          },
        });
        enqueued += 1;
        // The bytes now live durably in the offline queue's encrypted store — safe to drop the native
        // record + delete the source file.
        try {
          await plugin.clearPending({ id: cap.id });
        } catch {
          /* best-effort: a leftover record is re-enqueued next drain; enqueue is idempotent on id */
        }
      } catch {
        // Enqueue failed — leave the native record in place so the next drain retries it.
      }
    }

    // Push everything just enqueued (plus any pre-existing queue backlog) through create/upload/finalize.
    // drainCaptureQueue self-guards (single-flight, no-op offline) and never throws.
    if (enqueued > 0) {
      await drainCaptureQueue();
    }
    return enqueued;
  } catch {
    return 0;
  } finally {
    draining = false;
  }
}

/** Arm the in-car microphone foreground service from JS (native-only, best-effort). The tile and
 *  notification can also start it with no WebView involvement. */
export async function startVoiceNoteCapture(): Promise<void> {
  try {
    const plugin = await getPlugin();
    if (!plugin) return;
    await plugin.start();
  } catch {
    /* plugin/native unavailable */
  }
}

/** Stop the in-car microphone foreground service from JS (native-only, best-effort). */
export async function stopVoiceNoteCapture(): Promise<void> {
  try {
    const plugin = await getPlugin();
    if (!plugin) return;
    await plugin.stop();
  } catch {
    /* best-effort */
  }
}

/** Current recording state, or false if unavailable. */
export async function isVoiceNoteRecording(): Promise<boolean> {
  try {
    const plugin = await getPlugin();
    if (!plugin) return false;
    const { recording } = await plugin.isRecording();
    return Boolean(recording);
  } catch {
    return false;
  }
}

/**
 * Initialise the native→WebView bridge: subscribe to the live "voiceNoteCaptured" event so a note
 * finished while the app is foreground drains immediately, and run one catch-up drain now for any note
 * captured while the app was killed. Native-only; no-op on web. Returns a disposer that removes the
 * listener. Mounted once in MobileShell.
 */
export function initVoiceNoteCaptureBridge(): () => void {
  if (!isNative()) return () => {};

  let disposed = false;
  let removeListener: (() => Promise<void>) | null = null;

  void (async () => {
    const plugin = await getPlugin();
    if (!plugin || disposed) return;
    try {
      const handle = await plugin.addListener('voiceNoteCaptured', () => {
        void drainVoiceNotes();
      });
      if (disposed) {
        void handle.remove();
      } else {
        removeListener = handle.remove;
      }
    } catch {
      /* listener unavailable — the cold-start / resume drains still recover captures */
    }
  })();

  // Catch up on anything captured while the WebView wasn't running.
  void drainVoiceNotes();

  return () => {
    disposed = true;
    if (removeListener) void removeListener();
  };
}
